import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import IntentionOutOfBedIcon from 'shared/components/icons/intention-out-of-bed-icon';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import { ScenarioType } from 'shared/enums/scenario-type';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import Scenario from 'features/organisation/models/scenario';
import OrganizationUnitScenarioIcon from 'features/residents/resident-settings/components/organization-unit-scenario-icon/organization-unit-scenario-icon';
import ScenarioName from 'features/scenario/views/scenario-name.component';
import styles from './organisation-details-scenarios-list.module.scss';

type Props = {
  viewingMode: ViewingMode;
  linkedScenarios: Scenario[] | undefined;
  unlinkedScenarios: Scenario[] | undefined;
  onUnlinkButtonClicked: (selectedScenario: Scenario) => void;
  onRelinkButtonClicked: (selectedScenario: Scenario) => void;
};

const OrganisationDetailsScenariosList = ({
  viewingMode,
  linkedScenarios,
  unlinkedScenarios,
  onUnlinkButtonClicked,
  onRelinkButtonClicked,
}: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('organisation');
  const { hasPermission } = usePermissionAuthorization();

  const { control } = useFormContext();

  const isViewing = viewingMode === 'viewing';
  const hasAnyScenarios = (linkedScenarios && linkedScenarios.length > 0) || (unlinkedScenarios && unlinkedScenarios?.length > 0);
  const isDisableLinkScenario = isViewing || !hasPermission(Permission.LinkScenario);

  const linkedScenarioRow = (linkedScenario: Scenario): React.JSX.Element => (
    <div key={linkedScenario.id} className={styles.scenario}>
      <div className={styles.row}>
        <OrganizationUnitScenarioIcon isActive scenarioType={linkedScenario.scenarioType} />
        <Typography variant="subtitle1" data-testid={`scenarioTypeTextFor${linkedScenario.scenarioType}`}>
          <ScenarioName scenarioType={linkedScenario.scenarioType} />
        </Typography>
      </div>
      {!isViewing && hasPermission(Permission.LinkScenario) && (
        <Button startIcon={<LinkOffOutlinedIcon />} onClick={() => onUnlinkButtonClicked(linkedScenario)} variant="text">
          {t('scenariosList.unlinkButtonText')}
        </Button>
      )}
    </div>
  );

  if (!hasAnyScenarios) {
    return <Typography variant="subtitle1">{t('scenariosList.noScenariosLinked')}</Typography>;
  }

  return (
    <div className={styles.container}>
      {linkedScenarios?.map((linkedScenario) => {
        if (linkedScenario.scenarioType === ScenarioType.Bed) {
          return (
            <Accordion key={linkedScenario.id} disableGutters>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  padding: 0,
                  minHeight: '44px',
                  height: '44px',

                  '&.Mui-expanded': {
                    minHeight: '44px',
                    height: '44px',
                  },

                  '& .MuiAccordionSummary-content': {
                    margin: '0',

                    '&.Mui-expanded': {
                      margin: '0',
                    },
                  },
                }}
              >
                {linkedScenarioRow(linkedScenario)}
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <Controller
                  name="intentionOutOfBedEnabled"
                  control={control}
                  render={({ field }) => (
                    <Grid container direction="column" className={styles.settingsColumn} justifyContent="center">
                      <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        gap={2}
                        className={styles.settingsRow}
                      >
                        <IntentionOutOfBedIcon />
                        <Grid size="grow">
                          <Typography variant="subtitle1">{t('scenariosList.intentionOutOfBed')}</Typography>
                        </Grid>
                        <Grid>
                          <Switch {...field} type="checkbox" value={field.value} checked={field.value} disabled={isDisableLinkScenario} />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                />
              </AccordionDetails>
            </Accordion>
          );
        }
        return linkedScenarioRow(linkedScenario);
      })}

      {unlinkedScenarios?.map((unlinkedScenario) => {
        const { id, scenarioType } = unlinkedScenario;

        return (
          <div key={id} className={styles.scenario}>
            <div className={styles.row}>
              <OrganizationUnitScenarioIcon isActive scenarioType={scenarioType} />
              <Typography variant="subtitle1" className={styles.unlinked} data-testid={`scenarioTypeTextFor${scenarioType}`}>
                <ScenarioName scenarioType={scenarioType} />
              </Typography>
            </div>

            {!isViewing && hasPermission(Permission.LinkScenario) && (
              <Button startIcon={<LinkOutlined />} onClick={() => onRelinkButtonClicked(unlinkedScenario)} variant="text">
                {t('scenariosList.relinkButtonText')}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OrganisationDetailsScenariosList;
